import { useCallback, useMemo, useState } from 'react';

import { useUser } from 'context/user';
import { useTheraFernClient } from 'hooks/useFern';
import { useRouteAuthorization } from 'hooks/useRouteAuthorization';
import { useNavigation } from '../NavigationContext';
import { getShortcutGroups, ShortcutOption } from './shortcutOptions';

export const MAX_NAV_SHORTCUTS = 20;

export const useNavShortcuts = () => {
  const { user, setUser } = useUser() || {};
  const { navigation } = useNavigation();
  const { isAllowed } = useRouteAuthorization();
  const { theraFernApi } = useTheraFernClient();
  const [isSaving, setIsSaving] = useState(false);

  const groups = useMemo(() => getShortcutGroups(navigation ?? [], isAllowed), [navigation, isAllowed]);

  const optionsByHref = useMemo(
    () =>
      new Map<string, ShortcutOption>(groups.flatMap((group) => group.options.map((option) => [option.href, option]))),
    [groups],
  );

  const savedPaths = useMemo(() => user?.navShortcuts ?? [], [user?.navShortcuts]);

  const hiddenPaths = useMemo(() => savedPaths.filter((href) => !optionsByHref.has(href)), [savedPaths, optionsByHref]);

  const shortcuts = useMemo(
    () => savedPaths.map((href) => optionsByHref.get(href)).filter(Boolean),
    [savedPaths, optionsByHref],
  );

  const saveShortcuts = useCallback(
    async (selectedPaths: string[]) => {
      setIsSaving(true);
      try {
        const saved = await theraFernApi.thera.user.userApi.setNavShortcuts({
          shortcuts: [...selectedPaths, ...hiddenPaths],
        });
        if (user) setUser({ ...user, navShortcuts: saved });
      } finally {
        setIsSaving(false);
      }
    },
    [theraFernApi, hiddenPaths, user, setUser],
  );

  const maxSelectable = MAX_NAV_SHORTCUTS - hiddenPaths.length;

  return { groups, shortcuts, saveShortcuts, isSaving, maxSelectable };
};
